import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Input, Select, DatePicker, Button, Space, Typography, App, Empty } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import {
  CommunicationLogDto,
  CommunicationLogFilters,
  fetchAllCommunications,
} from '../modules/notifications/api/communicationsApi';
import CommunicationDetail, {
  CommunicationStatusTag,
} from '../modules/notifications/components/CommunicationDetail';
import { getCommunicationEventTypeOptions } from '../modules/notifications/notificationEventTypes';
import { canViewProductionDataLists } from '../modules/tester/testerModeAccessPolicy';
import { authService } from '../services/auth';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const PAGE_SIZE = 50;

interface Filters {
  recipient: string;
  orderNumber: string;
  channel?: string;
  status?: string;
  eventType?: string;
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
}

const EMPTY_FILTERS: Filters = {
  recipient: '',
  orderNumber: '',
  channel: undefined,
  status: undefined,
  eventType: undefined,
  dateRange: null,
};

const CommunicationsLog: React.FC = () => {
  const { t } = useTranslation(['notifications', 'common']);
  const { message } = App.useApp();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<CommunicationLogDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [selected, setSelected] = useState<CommunicationLogDto | null>(null);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  // Filters actually applied to the query (committed on Search), so paging stays stable.
  const [appliedFilters, setAppliedFilters] = useState<Filters>(EMPTY_FILTERS);

  // TESTER must not see production data, even via a direct URL (the menu item
  // is already hidden for this role).
  const canViewProductionData = canViewProductionDataLists(authService.getCurrentUser());

  // Financial event types are only selectable for users with financial
  // visibility; the backend excludes them for everyone else regardless.
  const eventTypeOptions = getCommunicationEventTypeOptions(authService.canViewMargin());

  const buildQuery = useCallback((f: Filters, offset: number): CommunicationLogFilters => {
    const query: CommunicationLogFilters = { limit: PAGE_SIZE, offset };
    if (f.recipient.trim()) query.recipient = f.recipient.trim();
    if (f.orderNumber.trim()) query.orderNumber = f.orderNumber.trim();
    if (f.channel) query.channel = f.channel;
    if (f.status) query.status = f.status;
    if (f.eventType) query.eventType = f.eventType;
    if (f.dateRange && f.dateRange[0]) query.dateFrom = f.dateRange[0].startOf('day').toISOString();
    if (f.dateRange && f.dateRange[1]) query.dateTo = f.dateRange[1].endOf('day').toISOString();
    return query;
  }, []);

  const load = useCallback(async (f: Filters, offset: number, append: boolean) => {
    setLoading(true);
    try {
      const data = await fetchAllCommunications(buildQuery(f, offset));
      setHasMore(data.length === PAGE_SIZE);
      setLogs((prev) => (append ? [...prev, ...data] : data));
    } catch {
      message.error(t('notifications:errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [buildQuery, t]);

  // Initial mount intentionally loads the unfiltered view; subsequent loads go
  // through Search/Reset, which commit `appliedFilters` before fetching.
  useEffect(() => {
    if (!canViewProductionData) return;
    load(EMPTY_FILTERS, 0, false);
  }, [load, canViewProductionData]);

  const handleSearch = () => {
    setAppliedFilters(filters);
    load(filters, 0, false);
  };

  const handleReset = () => {
    setFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    load(EMPTY_FILTERS, 0, false);
  };

  const handleLoadMore = () => {
    load(appliedFilters, logs.length, true);
  };

  const columns = [
    {
      title: t('notifications:log_page.sentAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) => new Date(value).toLocaleString(),
    },
    {
      title: t('notifications:log_page.recipient'),
      key: 'recipient',
      render: (_: unknown, record: CommunicationLogDto) =>
        record.userName ? `${record.userName} (${record.recipient})` : record.recipient,
    },
    {
      title: t('notifications:log_page.eventType'),
      dataIndex: 'eventType',
      key: 'eventType',
      render: (value: string) => t(`notifications:eventTypes.${value}`, value),
    },
    {
      title: t('notifications:log_page.orderNumber'),
      key: 'orderNumber',
      render: (_: unknown, record: CommunicationLogDto) =>
        record.orderId && record.orderNumber ? (
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={(e) => { e.stopPropagation(); navigate(`/app/orders/${record.orderId}`); }}
          >
            {record.displayOrderNumber || record.orderNumber}
          </Button>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: t('notifications:log_page.channel'),
      dataIndex: 'channel',
      key: 'channel',
      render: (value: string) => t(`notifications:channels.${value}`, value),
    },
    {
      title: t('notifications:log_page.status'),
      dataIndex: 'status',
      key: 'status',
      render: (value: string) => <CommunicationStatusTag status={value} />,
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_: unknown, record: CommunicationLogDto) => (
        <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); setSelected(record); }}>
          {t('notifications:comms.view')}
        </Button>
      ),
    },
  ];

  if (!canViewProductionData) {
    return (
      <div>
        <Title level={3}>{t('notifications:comms.pageTitle')}</Title>
        <Card style={{ textAlign: 'center', padding: 40, marginTop: 16 }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('notifications:comms.testerMode')}
          />
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Title level={3}>{t('notifications:comms.pageTitle')}</Title>
      <Text type="secondary">{t('notifications:comms.pageDescription')}</Text>

      <Card size="small" style={{ margin: '16px 0' }}>
        <Space wrap>
          <Input
            placeholder={t('notifications:comms.filterRecipient')}
            value={filters.recipient}
            onChange={(e) => setFilters({ ...filters, recipient: e.target.value })}
            onPressEnter={handleSearch}
            allowClear
            style={{ width: 200 }}
          />
          <Input
            placeholder={t('notifications:comms.filterOrder')}
            value={filters.orderNumber}
            onChange={(e) => setFilters({ ...filters, orderNumber: e.target.value })}
            onPressEnter={handleSearch}
            allowClear
            style={{ width: 160 }}
          />
          <RangePicker
            value={filters.dateRange}
            onChange={(dates) => setFilters({ ...filters, dateRange: dates })}
          />
          <Select
            placeholder={t('notifications:log_page.channel')}
            value={filters.channel}
            onChange={(value) => setFilters({ ...filters, channel: value })}
            allowClear
            style={{ width: 140 }}
            options={[
              { value: 'email', label: t('notifications:channels.email') },
              { value: 'telegram', label: t('notifications:channels.telegram') },
            ]}
          />
          <Select
            placeholder={t('notifications:log_page.status')}
            value={filters.status}
            onChange={(value) => setFilters({ ...filters, status: value })}
            allowClear
            style={{ width: 140 }}
            options={[
              { value: 'sent', label: t('notifications:status.sent') },
              { value: 'failed', label: t('notifications:status.failed') },
              { value: 'pending', label: t('notifications:status.pending') },
            ]}
          />
          <Select
            placeholder={t('notifications:log_page.eventType')}
            value={filters.eventType}
            onChange={(value) => setFilters({ ...filters, eventType: value })}
            allowClear
            showSearch
            style={{ width: 200 }}
            options={eventTypeOptions.map((ev) => ({
              value: ev,
              label: t(`notifications:eventTypes.${ev}`, ev),
            }))}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            {t('notifications:comms.search')}
          </Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            {t('notifications:comms.reset')}
          </Button>
        </Space>
      </Card>

      <Table
        dataSource={logs}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
        locale={{ emptyText: t('notifications:log_page.noLogs') }}
        onRow={(record) => ({ onClick: () => setSelected(record) })}
      />

      {hasMore && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <Button onClick={handleLoadMore} loading={loading}>
            {t('notifications:comms.loadMore')}
          </Button>
        </div>
      )}

      <CommunicationDetail
        record={selected}
        open={selected !== null}
        onClose={() => setSelected(null)}
      />
    </div>
  );
};

export default CommunicationsLog;
